import React from "react";
import { ReactLenis } from "lenis/react";
import { useReducedMotion } from "framer-motion";

/**
 * Site-wide smooth scrolling via Lenis (https://lenis.darkroom.engineering).
 *
 * Lenis animates the NATIVE scroll position (no transformed wrapper), so the
 * scrollbar, keyboard scrolling, `position: sticky/fixed`, find-in-page and
 * assistive tech all keep working — and every scroll-position consumer
 * (framer-motion `useScroll`, the image parallax, `ScrollWrapper`) picks the
 * smoothed values up for free. Touch devices are untouched: Lenis only
 * virtualizes mouse-wheel input by default (`syncTouch: false`), which is what
 * makes discrete wheel notches glide instead of jumping ~100px per frame.
 *
 * Accessibility / correctness:
 * - `useReducedMotion` visitors get plain native scrolling (Lenis never mounts).
 * - `anchors` keeps in-page `#hash` links scrolling (Lenis intercepts them otherwise).
 * - `allowNestedScroll` lets wheel input over scrollable overlays (dialogs, nav
 *   drawers, dropdowns) scroll THEM natively instead of the page behind. Add
 *   `data-lenis-prevent` to a scroller only if one misbehaves.
 * - `stopInertiaOnNavigate` stops in-flight inertia when a link is clicked, so
 *   leftover momentum can't fight Next's scroll-to-top on route change.
 * - Programmatic native scrolls (scroll restore, `scrollIntoView`) still work:
 *   Lenis syncs itself to scrolls it didn't initiate.
 */
const LENIS_OPTIONS = {
  autoRaf: true,
  anchors: true,
  allowNestedScroll: true,
  stopInertiaOnNavigate: true
} as const;
export function SmoothScroll() {
  const prefersReducedMotion = useReducedMotion();
  if (prefersReducedMotion) {
    return null;
  }
  return <ReactLenis root options={LENIS_OPTIONS} />;
}